// PANTALLA DE BLOQUEO — protege una sesión YA ABIERTA en un dispositivo que alguien coge.
//
// El razonamiento: la sesión dura mucho a propósito (nadie quiere volver a entrar cada día), así que
// el riesgo real no es que alguien adivine la contraseña, es que le roben el móvil a un trabajador
// con la app abierta. Esto cubre justo eso: tras unos minutos sin usarla, o al volver a la pestaña,
// la app se tapa y hay que desbloquear con la HUELLA / CARA del propio dispositivo (o, si no tiene,
// con la contraseña). Es la misma idea que el bloqueo del banco.
//
// Lo que NO es: esto no sustituye a poder CORTAR el dispositivo desde el servidor (eso está en
// Ajustes → Seguridad, y es lo que de verdad deja el móvil robado inservible). Es la primera barrera,
// la que actúa mientras nadie se ha dado cuenta todavía del robo.
import { useState, useEffect, useRef, useCallback } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { InputPassword, Ico } from './ui'
import { hayLector, desbloquearConHuella, registrarHuella, nombreDispositivo, estadoHuella, huellaDeEsteAparato, prepararHuella } from '../services/huella'
// Las CLAVES vienen de services/sesion, la lista unica de limpieza. Definirlas aqui — como estaban —
// las dejaba fuera de limpiarSesion(): la marca de bloqueo sobrevivia al cierre de la sesion y el
// candado saltaba otra vez nada mas volver a entrar con credenciales buenas.
import { CLAVE_BLOQUEO_MARCA, CLAVE_BLOQUEO_ACTIVO, marcarPantallaBloqueada } from '../services/sesion'
// L21 (4-oct-2026) · la contraseña que el llavero del iPhone sabe ofrecer y que se lee aunque React no se entere, y la
// cara o el dedo de la app del móvil (la llave de «Entrar con Face ID», 87463965) para desbloquear la MISMA sesión.
import { usuarioDeLaSesion, contrasenaDelFormulario } from '../utils/reautenticar'
import { hayBiometriaEnEstaApp, estadoEnEsteMovil, leerLlaves } from '../services/entradaBiometrica'
// 5-oct-2026 · el «Ahora no» de la oferta de la huella: 30 días en este navegador (el mismo que la oferta de dentro).
import { navegadorPospuesto, posponerEnNavegador } from '../utils/huellaPospuesta'
// t169b/t169c (12-oct-2026) · una hoja de la cara o el dedo a la vez, y lo que provoca la hoja no cuenta como volver a la
// aplicación (utils/faceIdUnaVez). Sale SOLA una vez por bloqueo, también en el iPhone.
import { hojaReciente } from '../utils/faceIdUnaVez'
// t347 (21-oct-2026) · LA «VUELTA DE VERDAD» YA NO ES DE 3 SEGUNDOS. El dueño: «en iPhone, cuando se bloquea la app y vuelves
// a entrar, sigue haciendo bucle en Face ID y se vuelve loco». Con la pantalla tapada, cualquier rato de 3 s con la página
// escondida que no cayera en la ventana de la hoja (utils/faceIdUnaVez · hojaReciente, 2,5 s) volvía a lanzar Face ID solo:
// lo que iOS mueve alrededor de la hoja (inactiva → activa, la página que se esconde y vuelve) fuera de esa ventana, o salir
// un momento a otra app, la relanzaba, y esa hoja nueva provocaba lo mismo. Ahora solo cuenta salir de verdad (la página
// escondida SIN hoja de Face ID abierta ni recién cerrada) y durante MÁS tiempo del que la clínica tiene puesto para el
// bloqueo (umbralSalidaMs, abajo): eso es otro acceso y se le ofrece Face ID otra vez, una. Si cancela o falla, se queda en
// el PIN con el botón. Lo mide backend/routes/face-id-sin-bucle-al-volver.test.js.
// t95 (5-oct-2026) · LA PANTALLA DEL TECLADO: desbloquear con el PIN de 6 cifras, «Cambiar de usuario» (tu nombre y tu PIN)
// y ponerse el PIN la primera vez. Solo sale cuando el servidor manda `pin_unico` en GET /seguridad/bloqueo (con su
// migración y en una cuenta de la clínica); sin eso, esta pantalla es exactamente la de siempre.
import { PantallaDelPin, EVENTO_CAMBIAR_USUARIO, EVENTO_PIN_PUESTO, anunciarCambioDeUsuario, cabeceraDispositivo } from '../utils/pantallaDelPin'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const MARCA = CLAVE_BLOQUEO_MARCA
// t169 (10-oct-2026) · «UN ACCESO» = abrir la aplicación o volver a ella tras más de este rato SIN USARLA (en esta pestaña o
// en cualquier otra del navegador: la marca de actividad es de todas). Es lo único que tapa la pantalla con «Fuera de la
// clínica, pedir la contraseña en CADA acceso». Un minuto: más que el aviso de la huella, la cámara o cambiar de pestaña.
const GRACIA_ACCESO_MS = 60 * 1000
const ultimaActividad = () => { try { return parseInt(localStorage.getItem(MARCA) || '0') || 0 } catch (_) { return 0 } }
// LA PANTALLA BLOQUEADA TIENE QUE SOBREVIVIR A UN F5. Antes `bloqueado` era solo estado de React:
// recargar montaba el componente desbloqueado y, peor, lo primero que hacia el vigilante era
// `tocar()` — reescribir la marca con la hora actual, asi que ni siquiera se miraba si el tiempo ya
// habia vencido. Resultado: con la sesion guardada en el navegador, F5 abria la app. Se guarda el
// ID de quien la dejo bloqueada: si entra OTRA persona, no hereda el bloqueo del anterior.
const ACTIVO = CLAVE_BLOQUEO_ACTIVO

export default function BloqueoPantalla() {
  const { user, token, logout, login } = useAuth()
  // El TOTEM del quiosco queda EXENTO: es la cuenta compartida de fichar (rol control_horario) y
  // las pantallas publicas de tablet. Sin esto, por la mañana el quiosco amanecia bloqueado
  // pidiendo la contraseña de la cuenta del totem (que el equipo no tiene) — los PIN de fichar no
  // valen para desbloquear. El quiosco no enseña datos clinicos: solo el panel de fichaje.
  // t136d (8-oct-2026) · SIN «limpieza»: es una persona de la clínica con su sesión (el servidor le aplica sus reglas, le manda
  // `pin_unico` y le anuncia «Cambiar de usuario»), y aquí estaba exenta como si fuera un tótem. Resultado: «Cambiar de
  // usuario» salía en su menú y no hacía nada, y una regla de bloqueo para Limpieza (o el puesto compartido donde entraba) no
  // la tapaba nunca. Exentos solo el quiosco de fichar (rol control_horario) y las pantallas públicas de tablet.
  const esTotem = user?.rol === 'control_horario'
    || /^\/tablet\//.test(window.location.pathname)
  const [minutos, setMinutos] = useState(0)
  // ── t302 (19-oct-2026) · EL CANDADO ES DE UNA PERSONA ──────────────────────────────────────────────────────────
  //  «Cambiar de usuario» abría la sesión de la persona nueva YA BLOQUEADA, con «La aplicación ya estaba bloqueada antes de
  //  volver a cargar la página». El candado era un `true` sin dueño: al entrar Laura con su PIN, `login()` cambia la persona
  //  y, en ESE pintado, `bloqueado` seguía en true (el reinicio de más abajo lo apaga, pero un efecto llega después del
  //  pintado). El efecto que guarda la marca para sobrevivir a un F5 corría en el mismo pintado, veía la pantalla tapada con
  //  la persona nueva y escribía `bloqueo_activo_de` = Laura justo antes de recargar. Ahora el candado apunta DE QUIÉN es y
  //  `bloqueado` solo vale true para esa persona: el de la anterior no tapa a la nueva ni un pintado, y nada lo puede copiar
  //  a su nombre. Lo mide backend/routes/cambiar-de-usuario-entra-abierta.test.js.
  const yo = String(user?.id || '')
  const yoRef = useRef(yo)
  yoRef.current = yo
  const [bloqueadoDe, setBloqueadoDe] = useState(null)
  const bloqueado = bloqueadoDe !== null && bloqueadoDe === yo
  const setBloqueado = useCallback((v) => { setBloqueadoDe(v ? yoRef.current : null) }, [])
  // `bio` = este aparato TIENE lector. `huellaLista` = ademas hay una huella registrada para esta
  // cuenta en este dominio. Antes solo se miraba lo primero, asi que el boton salia siempre y
  // fallaba siempre: pedia una credencial que nunca se habia dado de alta.
  const [bio, setBio] = useState(false)
  const [huellaLista, setHuellaLista] = useState(false)
  // ¿Se puede dar de alta una huella en este servidor? Es false cuando falta la migración de
  // WebAuthn: entonces el botón de alta solo puede devolver un 503, y ofrecerlo es una trampa.
  const [huellaDisponible, setHuellaDisponible] = useState(false)
  const [ofrecerAlta, setOfrecerAlta] = useState(false)
  // ── LA HUELLA, PEDIDA SOLA AL ABRIR (B-272) ─────────────────────────────────────────────────
  // `autoRef` = ya se ha intentado en ESTE bloqueo (una vez y no más: reintentar solo se sentiría
  // como un aparato que no deja teclear). `episodioRef` sube cada vez que el candado cambia, para
  // que la respuesta de un intento viejo no pinte un error encima de la pantalla de ahora.
  // `autoImposible` = el navegador ha dicho que no sin llegar a enseñar nada.
  const autoRef = useRef(false)
  const episodioRef = useRef(0)
  const [autoImposible, setAutoImposible] = useState(false)
  // t169c · `autoImposibleRef`: el navegador ya ha dicho que no a pedirla sola (Safari quiere un toque): en este bloqueo no se
  // vuelve a intentar sola, ni al volver. `vuelta` sube con cada vuelta de verdad a la aplicación (ver VUELTA_DE_VERDAD_MS).
  const autoImposibleRef = useRef(false)
  const [vuelta, setVuelta] = useState(0)
  const ocultoDesdeRef = useRef(0)
  // t347 · «en cada acceso» (fuera de la clínica), según la última respuesta del servidor: decide cuánto hay que estar fuera.
  const siempreRef = useRef(false)
  const [pass, setPass] = useState('')
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const timerRef = useRef(null)
  // t169 · lo que hace falta para no tapar sin motivo (ver el efecto que lee /seguridad/bloqueo): el candado de AHORA (para
  // las respuestas y los avisos de otras pestañas, que llegan fuera del pintado), cuándo se desbloqueó por última vez, los
  // minutos que ya se conocían, la llave anterior (¿se monta o se renueva?), la última escritura de la marca de actividad
  // y POR QUÉ se ha tapado (se le dice a quien lo ve, con el motivo del servidor).
  const bloqueadoRef = useRef(false)
  bloqueadoRef.current = bloqueado
  const abiertoAtRef = useRef(0)
  const minutosConocidosRef = useRef(null)
  const tokenPrevioRef = useRef(null)
  const ultimoToqueRef = useRef(0)
  const razonRef = useRef(null)
  // t347 · CUÁNTO HAY QUE ESTAR FUERA DE LA APLICACIÓN para que volver a ella sea otro acceso (y, con la pantalla tapada, se
  // vuelva a ofrecer Face ID solo, una vez): lo que la clínica tiene puesto para el bloqueo. Con «en cada acceso» (fuera de
  // la clínica) o sin minutos, el minuto de GRACIA_ACCESO_MS; con minutos, esos minutos. Nunca menos de ese minuto: la hoja
  // de Face ID dura segundos y no puede llegar a contar como una salida.
  const umbralSalidaMs = () => {
    const m = minutosConocidosRef.current
    if (siempreRef.current || !(m > 0)) return GRACIA_ACCESO_MS
    return Math.max(GRACIA_ACCESO_MS, m * 60000)
  }
  const [motivoServidor, setMotivoServidor] = useState({ motivo: '', fuera: '' })
  // ── L21 · LA CARA O EL DEDO DE LA APP DEL MÓVIL ─────────────────────────────────────────────
  // En la app (iPhone o Android) con «Entrar con Face ID o huella» activado, el móvil guarda una llave de esta cuenta
  // protegida por la biometría. Aquí se usa para DESBLOQUEAR la sesión que ya está abierta: el servidor comprueba que la
  // llave es de esta misma persona y de esta clínica (POST /auth/biometria/desbloquear). En el navegador no sale nada.
  const [nativa, setNativa] = useState({ lista: false, nombre: t('components.BloqueoPantalla.la_huella_o_la_cara', 'la huella o la cara') })
  const autoNativaRef = useRef(false)
  // L21 · el formulario de la contraseña de la tarjeta del PIN (para leer lo que deja el llavero).
  const formPinRef = useRef(null)

  // ── MODO ESTRICTO: HUELLA O PIN, LA CONTRASEÑA YA NO ────────────────────────────────────────
  // EL FALLO QUE ESTO ARREGLA, con las palabras del dueño: "no sirve de nada que la app me bloquee
  // cada 15 minutos si las claves están guardadas y si quieren acceder es tan simple como usar la
  // clave guardada". Tenía razón: esta pantalla SIEMPRE ofrecía "Desbloquear con contraseña", y esa
  // contraseña es exactamente la que el navegador rellena solo. El candado era un adorno.
  //
  // Con el modo encendido en Ajustes → Seguridad, quien tiene huella o PIN desbloquea con eso y la
  // contraseña deja de ofrecerse. NADIE SE QUEDA ENCERRADO: solo se exige a quien TIENE con qué, el
  // PIN funciona en cualquier aparato (con lector o sin él) y "Salir de la sesión" sigue estando
  // siempre abajo. El servidor aplica la misma regla en /auth/verificar-password, así que esto no
  // es solo pintura de pantalla.
  const [exigirFactor, setExigirFactor] = useState(false)
  const [tengoPin, setTengoPin] = useState(false)
  // Solo mientras llega la respuesta del servidor: el mínimo de verdad lo pone la clínica.
  const [pinMin, setPinMin] = useState(4)
  const [pin, setPin] = useState('')
  const [pin2, setPin2] = useState('')
  const [ofrecerPin, setOfrecerPin] = useState(false)
  const [passPin, setPassPin] = useState('')
  // La contraseña que se acaba de teclear, para poder guardar el PIN sin volver a pedirla en la
  // pantalla siguiente. Se borra en cuanto se usa: no tiene por qué sobrevivir al desbloqueo.
  const passRef = useRef('')
  // ── t95 · EL PIN ÚNICO Y «CAMBIAR DE USUARIO» ──────────────────────────────────────────────
  // `pinUnico` = lo que dice el servidor (pin_unico): si tengo PIN propio, si hay que ponerlo ya, la regla de mi rol o la
  // mía (minutos, obligatorio, huella/Face ID, cambiar de usuario) y si aquí se puede cambiar de usuario. null = como hoy.
  // `modoPin` = 'desbloquear' o 'elegir' (se ha pedido «Cambiar de usuario»). `verContrasena` = ha pulsado «Usar mi
  // contraseña» en el teclado: se enseña la tarjeta de siempre.
  const [pinUnico, setPinUnico] = useState(null)
  // El recordatorio de crear el PIN, escondido con «Ahora no» hasta que se vuelva a abrir la aplicación.
  const [recordatorioFuera, setRecordatorioFuera] = useState(() => {
    try { return sessionStorage.getItem('pin_recordado_' + (user?.id || '')) === '1' } catch (_) { return false }
  })
  const [modoPin, setModoPin] = useState('desbloquear')
  const [verContrasena, setVerContrasena] = useState(false)
  // t95c · AL ENTRAR CON LA CONTRASEÑA, a quien no tiene PIN se le OFRECE crearlo en ese momento (el dueño, 5-oct): «Ahora
  // no» lo deja para la próxima vez que entre con su contraseña (se apunta en esta pestaña del navegador). t217 (B-1637): ya
  // solo en un puesto compartido (`pinUnico.pedir`), y nunca obligado; fuera de él, el recordatorio pequeño.
  const [ofertaFuera, setOfertaFuera] = useState(() => {
    try { return sessionStorage.getItem('pin_ofrecido_' + (user?.id || '')) === '1' } catch (_) { return false }
  })
  // t95 · «AHORA TIENES UN SOLO PIN PARA TODO», una sola vez (el dueño, 5-oct): al desbloquear con el PIN de desbloqueo de
  // antes, que en ese momento pasa a ser su PIN para todo (o, si no puede, se le dice que elija uno). null = nada que decir.
  const [avisoUnico, setAvisoUnico] = useState(null)
  // t217 (B-1637) · «¿No recuerdas tu PIN?»: quien TIENE PIN y desbloquea con la contraseña desde el teclado. Una vez por pestaña.
  const [avisoOlvido, setAvisoOlvido] = useState(false)
  const avisarUnico = (d) => {
    if (!d || d.unificado == null) return
    const k = 'pin_unico_avisado_' + (user?.id || '')
    try { if (d.unificado === true && localStorage.getItem(k) === '1') return; if (d.unificado === true) localStorage.setItem(k, '1') } catch (_) { /* sin almacén: se dice igual */ }
    setAvisoUnico(d.unificado === true ? 'unificado' : 'elige')
  }

  // Minutos de inactividad configurados por la clínica (0 = sin bloqueo). El servidor devuelve el
  // tiempo que toca segun DONDE estemos: en la clinica se puede estar 45-60 min con un paciente sin
  // tocar la app, y ahi bloquear estorba; fuera interesa que se bloquee pronto.
  // Se vuelve a preguntar cada 10 min y al volver a la pestana, porque el mismo movil sale de la
  // clinica y entonces debe pasar al tiempo corto sin esperar a recargar la pagina.
  //
  // ── t169 (10-oct-2026) · LO QUE CONTESTA EL SERVIDOR YA NO TAPA LA PANTALLA SIN MOTIVO ─────────────────────────────
  //  El dueño: «estoy en login, pongo la huella, me lleva a la pestaña para poner mi contraseña, me saca y hago un bucle
  //  raro como 4-5 veces hasta que entra» (R8) y «en horario de trabajo no bloquee al resto, sin embargo nos saca de vez en
  //  cuando a poner el PIN en otros roles» (R9). Dos caminos tapaban sin que nadie llevara un rato sin tocar la aplicación:
  //   · «Fuera de la clínica, en CADA acceso» (`siempre`) tapaba con CADA respuesta: la de nada más entrar (con la huella
  //     o la contraseña recién puestas), la de la llave que se renueva sola al arrancar, el repaso de cada 10 minutos y la
  //     de cada vuelta a la pestaña (también la que provoca el propio aviso de la huella o de Face ID). Se entraba, se
  //     tapaba, se ponía el dedo, se tapaba otra vez... Ahora solo en un ACCESO: al abrir la aplicación o al volver a ella
  //     tras más de GRACIA_ACCESO_MS sin usarla (en esta pestaña o en otra), y nunca si entretanto se ha desbloqueado.
  //   · los minutos que CAMBIAN con la aplicación abierta (la red o el horario dicen «fuera» un momento, o cambia la regla):
  //     la marca de actividad no se apuntaba mientras tocaba 0 minutos, así que al pasar a 15 era de hacía horas y la
  //     pantalla se tapaba EN EL ACTO a quien estaba escribiendo. Ahora la actividad se apunta siempre (abajo) y, si los
  //     minutos cambian con la aplicación abierta, la cuenta empieza en ese momento.
  //  `origen` = por qué se pregunta ('montar', 'token', 'reloj', 'volver', 'pin'); `acceso` = { at } si ESE momento es
  //  un acceso (se decide al ocurrir, no al llegar la respuesta: tocar la pantalla mientras viaja no lo borra).
  useEffect(() => {
    if (!token) { tokenPrevioRef.current = null; return }
    let vivo = true
    const origenAlMontar = tokenPrevioRef.current ? 'token' : 'montar'
    tokenPrevioRef.current = token
    const esAcceso = () => {
      const ult = ultimaActividad()
      return ult && Date.now() - ult > GRACIA_ACCESO_MS ? { at: Date.now() } : null
    }
    // t95 · con la llave de ESTE dispositivo: la clínica puede tener una regla para este ordenador en concreto.
    const leer = (origen = 'reloj', acceso = null) => api.get('/seguridad/bloqueo', { headers: cabeceraDispositivo() })
      .then(r => {
        if (!vivo) return
        const nuevos = parseInt(r.data?.minutos) || 0
        const antes = minutosConocidosRef.current
        minutosConocidosRef.current = nuevos
        siempreRef.current = r.data?.siempre === true
        // Los minutos han cambiado con la aplicación abierta: se cuenta desde ahora (ver arriba). Solo con la pantalla
        // abierta: a una pantalla ya tapada no se le regala nada.
        if (antes !== null && antes !== nuevos && nuevos > 0 && !bloqueadoRef.current) tocar(true)
        setMinutos(nuevos)
        setMotivoServidor({ motivo: typeof r.data?.motivo === 'string' ? r.data.motivo : '', fuera: typeof r.data?.por_que_fuera === 'string' ? r.data.por_que_fuera : '' })
        // La politica de "huella o PIN obligatorios" viaja en esta MISMA respuesta a proposito: una
        // llamada mas al montar el candado es una llamada mas que puede fallar justo cuando hay que
        // desbloquear con prisa, y entonces la pantalla no sabria que ofrecer.
        setExigirFactor(r.data?.exigir_factor === true)
        setTengoPin(r.data?.tengo_pin === true)
        if (parseInt(r.data?.pin_min)) setPinMin(parseInt(r.data.pin_min))
        setPinUnico(r.data?.pin_unico && r.data.pin_unico.disponible ? r.data.pin_unico : null)
        // t95 · y se anuncia al menú del perfil (components/Layout): «Cambiar de usuario» sale siempre en una cuenta de la
        // clínica (si aquí no se puede pasar a otra persona, la pantalla lo dice).
        anunciarCambioDeUsuario(!!(r.data?.pin_unico && r.data.pin_unico.disponible))
        // Fuera de la clinica y con "contrasena en cada acceso": se bloquea YA, sin esperar minutos... pero solo en un
        // ACCESO (t169): ni recién entrado, ni al renovarse la llave, ni en el repaso, ni si se ha desbloqueado después.
        if (r.data?.siempre === true && acceso && !(abiertoAtRef.current >= acceso.at) && !bloqueadoRef.current) {
          razonRef.current = 'acceso'
          setBloqueado(true)
        }
      })
      .catch(() => { if (vivo) setMinutos(0) })
    leer(origenAlMontar, origenAlMontar === 'montar' ? esAcceso() : null)
    hayLector().then(b => { if (vivo) setBio(b) })
    estadoHuella().then(e => {
      if (!vivo) return
      setHuellaLista((e?.credenciales || 0) > 0)
      setHuellaDisponible(e?.disponible !== false)
    })
    const iv = setInterval(() => leer('reloj'), 10 * 60 * 1000)
    // t169b · si la página «vuelve» porque se cierra la hoja de Face ID o de la huella, no es un acceso: no se tapa por eso.
    const alVolver = () => { if (document.visibilityState === 'visible') leer('volver', hojaReciente() ? null : esAcceso()) }
    document.addEventListener('visibilitychange', alVolver)
    // t95 · quien acaba de poner su PIN en Mi perfil deja de ver el recordatorio en el acto.
    const conVentana = typeof window !== 'undefined' && window.addEventListener
    const trasPin = () => leer('pin')
    if (conVentana) window.addEventListener(EVENTO_PIN_PUESTO, trasPin)
    return () => {
      vivo = false; clearInterval(iv); document.removeEventListener('visibilitychange', alVolver)
      if (conVentana) window.removeEventListener(EVENTO_PIN_PUESTO, trasPin)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // L21 · ¿Esta app del móvil tiene la llave de esta cuenta? (Sin pedir la cara: solo mira si está guardada.)
  useEffect(() => {
    if (!token) return undefined
    let vivo = true
    if (!hayBiometriaEnEstaApp()) { setNativa({ lista: false, nombre: t('components.BloqueoPantalla.la_huella_o_la_cara', 'la huella o la cara') }); return undefined }
    estadoEnEsteMovil('equipo')
      .then(e => { if (vivo) setNativa({ lista: !!(e && e.app && e.puede && e.activada), nombre: (e && e.nombre) || t('components.BloqueoPantalla.la_huella_o_la_cara', 'la huella o la cara') }) })
      .catch(() => { if (vivo) setNativa({ lista: false, nombre: t('components.BloqueoPantalla.la_huella_o_la_cara', 'la huella o la cara') }) })
    return () => { vivo = false }
  }, [token, user?.id])

  // La marca de actividad, como mucho una escritura por segundo (se llama en cada tecla y en cada scroll). `forzar` la
  // escribe siempre: al desbloquear y al cambiar los minutos tiene que quedar la hora exacta.
  const tocar = useCallback((forzar) => {
    const ahora = Date.now()
    if (forzar !== true && ahora - ultimoToqueRef.current < 1000) return
    ultimoToqueRef.current = ahora
    try { localStorage.setItem(MARCA, String(ahora)) } catch (_) {}
  }, [])

  // ── ABRIR LA SESION: LAS DOS COSAS, SIEMPRE JUNTAS ─────────────────────────────────────────
  //
  //  EL FALLO QUE ESTO ARREGLA, encontrado ejecutando la pantalla (routes/huella-sola-al-abrir):
  //  desbloquear con la HUELLA o con el PIN se quedaba a medias. Ponian `setBloqueado(false)` pero
  //  NO borraban la marca `bloqueo_activo_de`, y el vigilante de inactividad —que esta declarado
  //  ANTES en este mismo fichero, o sea que su efecto corre primero— vuelve a leer esa marca en
  //  cuanto `bloqueado` cambia: la encontraba puesta y volvia a tapar la pantalla EN EL ACTO. Desde
  //  fuera: pones el dedo, el servidor da la firma por buena, y el candado sigue ahi. La contrasena
  //  no lo sufria porque borra la marca a mano (y una guardia fija esas dos lineas suyas).
  //  Aqui van juntas para que no se pueda volver a olvidar en el siguiente camino que se anada.
  // t169 · y se apunta CUÁNDO: una respuesta de «en cada acceso» que salió antes de abrir ya no vuelve a tapar.
  const abrir = useCallback(() => {
    tocar(true)
    abiertoAtRef.current = Date.now()
    try { localStorage.removeItem(ACTIVO) } catch (_) {}
    setBloqueado(false)
  }, [tocar])

  // ESTE COMPONENTE NO SE DESMONTA NUNCA: vive fuera de las rutas (App.jsx), así que su estado
  // sobrevive a cerrar sesión y a entrar como otra persona. Sin esto, `bloqueado` se quedaba en
  // true al salir y la pantalla de bloqueo reaparecía ENCIMA de la sesión recién abierta — el
  // segundo camino al mismo bucle, y el que hacía que "cerrar sesión y entrar de cero" tampoco
  // bastara a veces. Cambiar de sesión (o quedarse sin ella) empieza de cero: sin candado.
  // t169 · RENOVAR LA LLAVE NO ES CAMBIAR DE SESIÓN: la misma persona sigue y su candado también (antes, cada renovación
  // destapaba la pantalla un instante y borraba el PIN que estaba escribiendo). Cuenta que haya sesión y de quién es.
  const haySesion = !!token
  useEffect(() => {
    setBloqueado(false); setOfrecerAlta(false); setOfrecerPin(false)
    setPass(''); setPin(''); setPin2(''); setPassPin(''); setError('')
    passRef.current = ''
    setModoPin('desbloquear'); setVerContrasena(false); setAvisoOlvido(false)
    minutosConocidosRef.current = null; siempreRef.current = false; razonRef.current = null
    anunciarCambioDeUsuario(false)   // t95 · hasta que el servidor diga otra cosa para ESTA sesión
    try { setRecordatorioFuera(sessionStorage.getItem('pin_recordado_' + (user?.id || '')) === '1') } catch (_) { setRecordatorioFuera(false) }
  }, [haySesion, user?.id])

  // t95 · «CAMBIAR DE USUARIO» A MANO (el menú de la foto, Mi perfil): se tapa la pantalla en el acto con «¿Quién eres?».
  // Desde ese momento es un candado como el de la inactividad: seguir como la misma persona pide su PIN.
  useEffect(() => {
    if (!token || esTotem || typeof window === 'undefined' || !window.addEventListener) return undefined
    const abrirCambio = () => { razonRef.current = 'cambio'; setModoPin('elegir'); setVerContrasena(false); setError(''); setBloqueado(true) }
    window.addEventListener(EVENTO_CAMBIAR_USUARIO, abrirCambio)
    return () => window.removeEventListener(EVENTO_CAMBIAR_USUARIO, abrirCambio)
  }, [token, esTotem])
  // t95 · Y SOBREVIVE A UN F5 aunque esta persona no tenga bloqueo por inactividad (el vigilante de abajo solo mira la
  // marca cuando hay minutos): quien lo abrió y se fue no deja la sesión abierta a quien recargue.
  // (t169 · Sin el PIN único y sin minutos no hace falta: «en cada acceso» vuelve a tapar al abrir la página, porque mientras
  // está tapada no se apunta actividad. Y una marca que se quedó pegada con el bloqueo apagado no tapa a nadie.)
  useEffect(() => {
    if (!token || esTotem || !pinUnico) return
    try { if (localStorage.getItem(ACTIVO) === String(user?.id || '')) { if (!bloqueadoRef.current && !razonRef.current) razonRef.current = 'recarga'; setBloqueado(true) } } catch (_) {}
  }, [token, user?.id, esTotem, pinUnico])
  // Al abrirse (con el PIN, la huella o la contraseña), la próxima vez empieza otra vez por el teclado.
  useEffect(() => { if (!bloqueado) { setVerContrasena(false); setModoPin('desbloquear'); razonRef.current = null } }, [bloqueado])

  // Vigila la inactividad y también la vuelta a la pestaña (el caso típico: el móvil se guarda en el
  // bolsillo y lo saca otra persona).
  useEffect(() => {
    if (!token || !minutos || bloqueado || esTotem) return
    // SE COMPRUEBA ANTES DE TOCAR. Este `tocar()` estaba el primero y borraba la prueba: al recargar
    // reescribia la marca de actividad con la hora de AHORA, asi que el tiempo vencido desaparecia.
    // Dos motivos para quedarse bloqueado al montar: (a) la pantalla ya estaba bloqueada cuando se
    // recargo, o (b) el tiempo de inactividad ya habia vencido mientras no estaba montado.
    let sigueBloqueado = false
    try { sigueBloqueado = localStorage.getItem(ACTIVO) === String(user?.id || '') } catch (_) {}
    if (!sigueBloqueado) {
      const ult = parseInt(localStorage.getItem(MARCA) || '0')
      sigueBloqueado = !!ult && Date.now() - ult > minutos * 60000
    }
    if (sigueBloqueado && !razonRef.current) razonRef.current = 'inactividad'
    if (sigueBloqueado) { setBloqueado(true); return }
    tocar()
    const comprobar = () => {
      const ult = parseInt(localStorage.getItem(MARCA) || '0')
      if (ult && Date.now() - ult > minutos * 60000) { razonRef.current = 'inactividad'; setBloqueado(true) }
    }
    const onVisible = () => { if (document.visibilityState === 'visible' && !hojaReciente()) comprobar() }
    document.addEventListener('visibilitychange', onVisible)
    timerRef.current = setInterval(comprobar, 15000)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [token, minutos, bloqueado, tocar, esTotem, user?.id])

  // ── t169 · LA ACTIVIDAD SE APUNTA SIEMPRE, también con 0 minutos ─────────────────────────────────────────────
  //  Antes solo la apuntaba el vigilante (con minutos). Dentro de la clínica (0 minutos) la marca se quedaba con la hora de
  //  la última vez que hubo minutos, y en cuanto la red o el horario decían «fuera» un momento, el vigilante la encontraba
  //  vieja y tapaba EN EL ACTO a quien estaba trabajando (R9). Al montar NO se toca: un F5 con el tiempo vencido sigue
  //  tapando. Es la misma marca en todas las pestañas: trabajar en una cuenta para las demás.
  useEffect(() => {
    if (!token || esTotem || bloqueado || typeof window === 'undefined' || !window.addEventListener) return undefined
    const eventos = ['click', 'keydown', 'touchstart', 'scroll', 'pointerdown']
    eventos.forEach(e => window.addEventListener(e, tocar, { passive: true }))
    return () => { eventos.forEach(e => window.removeEventListener(e, tocar)) }
  }, [token, esTotem, bloqueado, tocar])

  // Se guarda DESPUES del vigilante, a proposito: en el mismo renderizado en que llegan los minutos,
  // el de arriba lee la marca antes de que este la pueda borrar.
  // t169 · Y SOLO SE ESCRIBE: la quita quien desbloquea (abrir, la contraseña, la sesión de otra persona, salir) y la limpieza
  // de la sesión. Antes también la borraba este efecto al repintarse sin candado con minutos, y entre pestañas eso sería
  // destapar la de al lado (que ahora escucha esta marca: abajo).
  useEffect(() => {
    // Sin token no hay sesión que proteger: escribir la marca aquí dejaba un candado a nombre de
    // nadie que luego heredaba el siguiente que entrase.
    if (esTotem || !token) return
    try {
      if (bloqueado) localStorage.setItem(ACTIVO, String(user?.id || ''))
    } catch (_) {}
  }, [bloqueado, minutos, esTotem, user?.id])

  // ── t169 · LAS PESTAÑAS VAN A LA VEZ ───────────────────────────────────────────────────────────────────────
  //  Con varias pestañas de Biango abiertas, cada una tenía su candado: se desbloqueaba una y las demás seguían tapadas, y
  //  al pasar a otra había que volver a poner la huella o el PIN (una vez por pestaña: «4 o 5 veces hasta que entra»).
  //  Ahora la marca del candado (ACTIVO, la de todas) manda: si otra pestaña se tapa, esta también; si otra se desbloquea
  //  —o se entra de nuevo— con la MISMA persona, esta se destapa. Si la que entra es OTRA persona, esta pestaña no se
  //  destapa enseñando lo de la anterior: se vuelve a cargar con la sesión nueva.
  useEffect(() => {
    if (!token || esTotem || typeof window === 'undefined' || !window.addEventListener) return undefined
    const mio = String(user?.id || '')
    const alCambiar = (e) => {
      if (!e || e.key !== ACTIVO) return
      if (e.newValue === mio && !bloqueadoRef.current) { razonRef.current = 'otra_pestana'; setBloqueado(true); return }
      if (e.newValue == null && bloqueadoRef.current) {
        let quien = ''
        try { quien = String((JSON.parse(localStorage.getItem('user') || 'null') || {}).id || '') } catch (_) { quien = '' }
        if (quien && quien !== mio) { try { window.location.reload() } catch (_) { /* sin ventana */ } return }
        if (!quien || !localStorage.getItem('token')) return
        abiertoAtRef.current = Date.now()
        setBloqueado(false)
      }
    }
    window.addEventListener('storage', alCambiar)
    return () => window.removeEventListener('storage', alCambiar)
  }, [token, esTotem, user?.id])

  // ── LO QUE PASA AQUI SE PUBLICA, PORQUE NO LO SABE NADIE MAS ───────────────────────────────
  // La puerta de los documentos personales (components/PuertaDocumentos) tiene que cerrarse si la
  // pantalla se tapa: ahi dentro hay nominas. Antes lo preguntaba mirando la clave del almacen, que
  // dice «se tapo alguna vez» y no «esta tapada ahora», y con el bloqueo de pantalla apagado esa
  // marca se queda pegada: al dueño le sacaba de sus documentos cada 15 segundos (14-sep-2026).
  // Este efecto NO tiene salidas tempranas a proposito —ni por totem ni por sesion—: si tuviera
  // alguna, el estado publicado se quedaria con el valor de antes, que es el fallo de nuevo.
  useEffect(() => { marcarPantallaBloqueada(!!bloqueado) }, [bloqueado])

  const avisar = (ok, metodo) => { api.post('/seguridad/desbloqueo', { ok, metodo }).catch(() => {}) }

  // La firma la comprueba el SERVIDOR (el reto lo emite él, es de un solo uso, y verifica que la
  // credencial sea de esta cuenta). Aquí no se puede dar nada por bueno: antes bastaba con que
  // `navigator.credentials.get()` devolviera algo, y eso se saltaba desde la consola del navegador.
  const desbloquearHuella = async () => {
    setError(''); setOcupado(true)
    try {
      const r = await desbloquearConHuella({ agenteId: user?.id })
      if (r?.enCurso) return   // t169b · ya hay una hoja abierta: este toque no pide otra
      if (r?.sinCredenciales) {
        setHuellaLista(false)
        setError(t('components.BloqueoPantalla.todavia_no_has_registrado_la', 'Todavía no has registrado la huella en este dispositivo. Entra con tu contraseña y te lo ofrezco.'))
      } else if (r?.ok) {
        abrir(); setPass('')
      } else {
        setError(t('components.BloqueoPantalla.no_se_ha_podido_verificar', 'No se ha podido verificar. Usa tu contraseña.'))
      }
    } catch (e) {
      // El aviso al servidor lo escribe la propia ruta de verificación: quien sabe si la firma era
      // buena es ella, no esta pantalla. Aquí solo quedan los fallos que ni llegan a salir (el
      // usuario cancela el diálogo del sistema, o el navegador no encuentra la credencial).
      setError(e?.response?.data?.error || t('components.BloqueoPantalla.no_se_ha_podido_verificar', 'No se ha podido verificar. Usa tu contraseña.'))
    } finally {
      setOcupado(false)
      // Si sigue tapada (se canceló o falló), el reto del siguiente toque se pide ya (ver «pedido antes del toque»).
      setTimeout(() => { if (bloqueadoRef.current) prepararHuella(user?.id) }, 0)
    }
  }

  // ── LA HUELLA SE PIDE SOLA AL ABRIR ────────────────────────────────────────────────────────
  //
  //  LO QUE PIDIÓ EL DUEÑO (B-272): «si alguien tiene activado la huella para acceder a la app
  //  debería mostrar directamente al abrir la app la huella para no tener que pulsar nada».
  //
  //  LAS TRES COSAS QUE NO PUEDEN PASAR, y por qué el código se complica justo ahí:
  //
  //  (a) QUE CANCELARLA ENCIERRE A NADIE. Este intento NO pone `ocupado`: la pantalla sigue
  //      entera y viva detrás del diálogo del sistema, así que en cuanto se cierra ya se puede
  //      escribir la contraseña (o el PIN) sin recargar ni esperar nada. Y se intenta UNA vez por
  //      bloqueo: insistir sería un aparato que no deja teclear.
  //
  //  (b) QUE SE PIDA DONDE NO PUEDE SALIR. La huella es de UN aparato. El servidor sabe cuántas
  //      tiene la cuenta, pero no en cuál estás: quien lo sabe es este navegador, porque la
  //      credencial se creó aquí (services/huella, `huellaDeEsteAparato`). Sin esa marca no se
  //      pide nada — el botón sigue ahí para quien quiera intentarlo a mano. Y antes de abrir el
  //      diálogo se comprueba contra el SERVIDOR que esa credencial siga viva.
  //
  //  (c) QUE ESTORBE EN EL ORDENADOR DE RECEPCIÓN. Ahí entran varias personas, pero el candado no
  //      es de "el aparato": es de la sesión abierta, y la marca se guarda por cuenta. Solo se le
  //      pide a quien registró su huella en ESE ordenador; a los demás, ni un diálogo.
  //
  //  Y EL NAVEGADOR PUEDE DECIR QUE NO. La pestaña tiene que estar delante (Chrome rechaza en seco
  //  si el documento no tiene el foco) y Safari exige que la ceremonia venga de un gesto. Cuando
  //  eso pasa, la promesa se rompe EN EL ACTO —sin haber enseñado nada— y aquí no se pinta ningún
  //  error rojo: se deja el botón, que sí lleva el gesto detrás, y una línea que lo explica.
  const pedirHuellaSola = async () => {
    const mio = episodioRef.current
    const desde = Date.now()
    try {
      const r = await desbloquearConHuella({ soloDeEsteAparato: true, agenteId: user?.id })
      if (mio !== episodioRef.current) return
      if (r?.enCurso) return
      if (r?.ok) { abrir(); setPass(''); setError(''); return }
      // Ninguno de estos dos es un fallo que contarle a nadie: es que aquí no había nada que pedir.
      if (r?.sinCredenciales) { setHuellaLista(false); return }
      if (r?.noEsDeEsteAparato) return
      setError(t('components.BloqueoPantalla.no_se_ha_podido_comprobar', 'No se ha podido comprobar la huella. Puedes intentarlo otra vez con el botón.'))
    } catch (e) {
      if (mio !== episodioRef.current) return
      // EL SERVIDOR HA CONTESTADO QUE NO. Eso no es política del navegador: es un motivo de verdad
      // (la firma, el reto caducado, la credencial de otra cuenta) y se dice con sus palabras. Sin
      // separar este caso, un rechazo del servidor —que en red local llega en milisegundos— se
      // disfrazaba de «tu navegador no deja», y nadie habría visto nunca el motivo real.
      if (e?.response) {
        setError(e.response.data?.error || t('components.BloqueoPantalla.no_se_ha_podido_comprobar', 'No se ha podido comprobar la huella. Puedes intentarlo otra vez con el botón.'))
        return
      }
      // ¿LLEGÓ A SALIR EL DIÁLOGO? Si el navegador rechaza antes de medio segundo sin que el
      // servidor haya dicho nada, no ha salido: nadie cancela tan deprisa. Es política del
      // navegador (Safari pide un gesto, Chrome pide el foco), no un fallo de la persona.
      if (Date.now() - desde < 400) { autoImposibleRef.current = true; setAutoImposible(true); return }
      setError(t('components.BloqueoPantalla.has_cerrado_el_aviso_de', 'Has cerrado el aviso de la huella. Puedes volver a intentarlo con el botón.'))
    } finally {
      // t169c · si sigue tapada, el reto del botón se pide ya (este intento pudo gastar el que había preparado).
      setTimeout(() => { if (bloqueadoRef.current) prepararHuella(user?.id) }, 0)
    }
  }

  // Cada vez que el candado cambia empieza un episodio nuevo: se puede volver a pedir sola, y las
  // respuestas del intento anterior ya no pintan nada.
  useEffect(() => {
    episodioRef.current += 1
    autoRef.current = false
    autoImposibleRef.current = false
    setAutoImposible(false)
  }, [bloqueado])

  // ── t169c · VOLVER DE VERDAD A LA APLICACIÓN CON LA PANTALLA TAPADA ──────────────────────────────────────────────
  //  El dueño (12-oct): «¿no puede salir automáticamente lo de Face ID sin tener que pulsar un botón?», pero sin el bucle.
  //  Sale SOLA una vez por bloqueo. Si se cancela (o falla) no se vuelve a pedir sola: quedan el botón y el PIN.
  //  t347 · Solo SALIR DE VERDAD de la aplicación es otro intento de entrar y la vuelve a ofrecer (una): la página escondida
  //  sin hoja de Face ID abierta ni recién cerrada, y fuera MÁS de lo que la clínica tiene puesto (umbralSalidaMs). Lo que
  //  pasa mientras está la hoja (iOS deja la app inactiva y la vuelve a activar, la página se esconde y vuelve) NO cuenta: si
  //  la página se esconde con una hoja abierta o recién cerrada, esa salida no se apunta. Antes bastaban 3 segundos (el
  //  bucle). Si el navegador ya dijo que no la deja pedir sola (Safari quiere un toque), tampoco.
  useEffect(() => {
    if (!token || esTotem || typeof document === 'undefined' || !document.addEventListener) return undefined
    const alCambiar = () => {
      if (document.visibilityState === 'hidden') { ocultoDesdeRef.current = hojaReciente() ? 0 : Date.now(); return }
      if (document.visibilityState !== 'visible') return
      const fuera = ocultoDesdeRef.current ? Date.now() - ocultoDesdeRef.current : 0
      ocultoDesdeRef.current = 0
      if (!bloqueadoRef.current || fuera <= umbralSalidaMs() || hojaReciente() || autoImposibleRef.current) return
      autoRef.current = false
      autoNativaRef.current = false
      setVuelta((n) => n + 1)
    }
    document.addEventListener('visibilitychange', alCambiar)
    return () => document.removeEventListener('visibilitychange', alCambiar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, esTotem])

  // ── t169c · EL RETO DE LA HUELLA, PEDIDO ANTES DEL TOQUE ─────────────────────────────────────────────────────────
  //  Safari (iPhone y Mac) solo abre la hoja si se pide DENTRO del toque, sin esperar a nada antes. Con la pantalla tapada se
  //  pide ya el reto al servidor (y se renueva antes de que caduque): al tocar «Desbloquear con huella o cara», la hoja sale
  //  en el acto (services/huella · prepararHuella).
  useEffect(() => {
    if (!bloqueado || !token || esTotem || !bio || !huellaLista) return undefined
    prepararHuella(user?.id)
    const iv = setInterval(() => prepararHuella(user?.id), 4 * 60 * 1000)
    return () => clearInterval(iv)
  }, [bloqueado, token, esTotem, bio, huellaLista, user?.id, vuelta])

  useEffect(() => {
    if (!bloqueado || !token || esTotem) return undefined
    if (!bio || !huellaLista) return undefined
    // t169c · también en el iPhone se intenta sola UNA vez por bloqueo; si Safari dice que no sin enseñar nada (quiere un
    // toque), se queda el botón y no se reintenta: ni una vuelta de verdad la vuelve a lanzar (`autoImposibleRef`, arriba).
    // t95 · la regla de esta persona puede no permitir la huella (Seguridad de acceso, por rol o por persona).
    if (pinUnico && (pinUnico.biometria === false || (pinUnico.regla && pinUnico.regla.biometria === false))) return undefined
    // SIN MARCA DE ESTE APARATO NO SE PIDE NADA (caso b).
    if (!huellaDeEsteAparato(user?.id)) return undefined
    if (autoRef.current) return undefined
    let vivo = true
    const lanzar = () => {
      if (!vivo || autoRef.current) return
      // La pestaña tiene que estar DELANTE. Si el candado saltó con la aplicación de fondo (el
      // vigilante corre igual), pedir la huella ahí se rechaza en seco y se gastaría el único
      // intento: se espera a que vuelva, que es justo "al abrir la app".
      const d = typeof document !== 'undefined' ? document : null
      if (d && d.visibilityState && d.visibilityState !== 'visible') return
      if (d && typeof d.hasFocus === 'function' && !d.hasFocus()) return
      if (hojaReciente()) return   // t169b · el foco que devuelve una hoja que se acaba de cerrar no es «abrir la app»
      autoRef.current = true
      pedirHuellaSola()
    }
    lanzar()
    if (autoRef.current || typeof document === 'undefined') return () => { vivo = false }
    document.addEventListener('visibilitychange', lanzar)
    if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('focus', lanzar)
    return () => {
      vivo = false
      document.removeEventListener('visibilitychange', lanzar)
      if (typeof window !== 'undefined' && window.removeEventListener) window.removeEventListener('focus', lanzar)
    }
  }, [bloqueado, token, esTotem, bio, huellaLista, user?.id, pinUnico, vuelta])

  // ── L21 · DESBLOQUEAR CON FACE ID O LA HUELLA DE LA APP ────────────────────────────────────
  // Lo pide el sistema del móvil (la cara o el dedo leen la llave guardada) y lo DECIDE EL SERVIDOR: la llave tiene que ser
  // de esta misma persona y de la clínica de esta sesión. Cancelar no es un error: se queda la contraseña a mano.
  const desbloquearNativa = async () => {
    setError(''); setOcupado(true)
    try {
      const l = await leerLlaves('equipo', { motivo: t('components.BloqueoPantalla.desbloquear_la_sesion', 'Desbloquear la sesión'), titulo: t('components.BloqueoPantalla.desbloquear', 'Desbloquear') })
      if (l && l.enCurso) return   // t169b · ya hay una hoja de Face ID abierta: no se pide otra
      if (l && Array.isArray(l.tokens) && l.tokens.length) {
        await api.post('/auth/biometria/desbloquear', { tokens: l.tokens })
        abrir(); setPass('')
      } else if (l && l.sinLlave) {
        setNativa(n => ({ ...n, lista: false }))
        setError(t('components.BloqueoPantalla.en_este_movil_ya_no', 'En este móvil ya no está activado entrar con {{nombre}}. Usa tu contraseña.', { nombre: nativa.nombre }))
      } else if (l && l.error) {
        setError(t('components.BloqueoPantalla.no_se_ha_podido_usar', 'No se ha podido usar {{nombre}}. Usa tu contraseña.', { nombre: nativa.nombre }))
      }
    } catch (e) {
      if (!e?.response) setError(t('components.BloqueoPantalla.no_hay_conexion_con_el', 'No hay conexión con el servidor. Espera unos segundos y vuelve a intentarlo.'))
      else {
        if (e.response.status === 403) setNativa(n => ({ ...n, lista: false }))
        setError(e.response.data?.error || t('components.BloqueoPantalla.no_se_ha_podido_comprobar_2', 'No se ha podido comprobar. Usa tu contraseña.'))
      }
    } finally { setOcupado(false) }
  }

  // Se pide SOLA una vez por bloqueo, con la app delante, como la huella del navegador (B-272). Si este aparato ya pide
  // esa otra sola, no se piden las dos.
  // t169c (12-oct-2026) · también en el iPhone (Face ID de la app no necesita un toque), UNA vez por bloqueo: si se cancela o
  // falla, no se repite sola (quedan el botón y el PIN) hasta una vuelta de verdad a la app. Si el bloqueo llega con la app
  // de fondo, se pide al volver a verla. Y nunca con una hoja abierta o recién cerrada: el foco y la visibilidad que mueve
  // la propia hoja no la vuelven a lanzar (utils/faceIdUnaVez · hojaReciente).
  // t347 · Y UN SOLO OYENTE. Si el bloqueo llega con la app escondida, se espera a que la página se vuelva a ver
  // (visibilitychange) y nada más: el `focus` que tenía también lo mueve la propia hoja de Face ID en iOS (la app pasa a
  // inactiva y vuelve), y con dos oyentes una sola vuelta se oía dos veces. El candado se apunta («pedida»,
  // autoNativaRef) ANTES de llamar al complemento, y con una hoja abierta o recién cerrada no se lanza otra.
  useEffect(() => { autoNativaRef.current = false }, [bloqueado])
  useEffect(() => {
    if (!bloqueado || !token || esTotem || !nativa.lista || autoNativaRef.current) return undefined
    if (pinUnico && (pinUnico.biometria === false || (pinUnico.regla && pinUnico.regla.biometria === false))) return undefined   // t95 · la regla no la permite
    if (bio && huellaLista && huellaDeEsteAparato(user?.id)) return undefined
    let vivo = true
    const lanzar = () => {
      if (!vivo || autoNativaRef.current) return
      const d = typeof document !== 'undefined' ? document : null
      if (d && d.visibilityState && d.visibilityState !== 'visible') return
      if (hojaReciente()) return   // abierta o recién cerrada (utils/faceIdUnaVez)
      autoNativaRef.current = true
      desbloquearNativa()
    }
    lanzar()
    if (autoNativaRef.current || typeof document === 'undefined') return () => { vivo = false }
    document.addEventListener('visibilitychange', lanzar)
    return () => {
      vivo = false
      document.removeEventListener('visibilitychange', lanzar)
    }
  }, [bloqueado, token, esTotem, nativa.lista, bio, huellaLista, user?.id, pinUnico, vuelta])

  // EL PIN ES EL RESPALDO QUE FUNCIONA EN CUALQUIER APARATO. La huella es de ESTE móvil; el
  // ordenador de recepción sin lector, o el día que el sensor no lee porque tienes guantes, se
  // quedarían sin salida. Lo comprueba el servidor (hash bcrypt, con freno de intentos): aquí no se
  // puede dar nada por bueno, igual que con la huella.
  const desbloquearPin = async () => {
    if (!pin) return setError(t('components.BloqueoPantalla.escribe_tu_pin', 'Escribe tu PIN'))
    setError(''); setOcupado(true)
    try {
      const r = await api.post('/seguridad/desbloqueo/pin/verificar', { pin })
      abrir(); setPin('')
      avisarUnico(r && r.data)
    } catch (e) {
      const st = e?.response?.status
      if (!e?.response) {
        setError(t('components.BloqueoPantalla.no_hay_conexion_con_el', 'No hay conexión con el servidor. Espera unos segundos y vuelve a intentarlo.'))
      } else if (st === 429) {
        setError(e.response.data?.error || t('components.BloqueoPantalla.demasiados_intentos_con_el_pin', 'Demasiados intentos con el PIN. Usa la huella o sal de la sesión.'))
      } else if (st === 401) {
        setError(t('components.BloqueoPantalla.tu_sesion_ha_caducado_te', 'Tu sesión ha caducado. Te llevo a la pantalla de entrada…'))
      } else if (st === 503 || e.response.data?.motivo === 'sin_pin') {
        // SALIDA DE EMERGENCIA. Si el servidor no tiene el PIN (falta la migración) o resulta que
        // esta cuenta no tiene ninguno, se apaga el modo estricto EN ESTA PANTALLA y vuelve a salir
        // la contraseña. Quedarse mirando un campo de PIN que el servidor no conoce, con un paciente
        // en el sillón, es el escenario que no puede pasar.
        setTengoPin(false)
        setError(t('components.BloqueoPantalla.el_pin_no_esta_disponible', 'El PIN no está disponible. Desbloquea con la huella o con tu contraseña.'))
      } else {
        setError(e.response.data?.error || t('components.BloqueoPantalla.pin_incorrecto', 'PIN incorrecto'))
      }
      // El fallo lo apunta la propia ruta del servidor, que es quien sabe si el PIN era bueno.
    } finally { setOcupado(false) }
  }

  // L21 · `claveLeida` = lo que hay DE VERDAD en la casilla al enviar el formulario (utils/reautenticar): con el llavero
  // del iPhone el valor está en la casilla y el estado de React puede seguir vacío. Era «obliga a ponerla otra vez».
  const desbloquearPass = async (claveLeida) => {
    const clave = (typeof claveLeida === 'string' && claveLeida) || pass
    if (!clave) return setError(t('components.BloqueoPantalla.escribe_tu_contrasena', 'Escribe tu contraseña'))
    setError(''); setOcupado(true)
    try {
      // Se valida contra el servidor con el email de la sesión. No crea sesión nueva: solo confirma
      // que quien está delante conoce la contraseña.
      await api.post('/auth/verificar-password', { password: clave }, { headers: cabeceraDispositivo() })
      // Se guarda para la pantalla siguiente (dar de alta el PIN la pide) y se borra en cuanto se
      // usa. Volver a pedirla dos pantallas después es como se consigue que nadie configure nada.
      passRef.current = clave
      tocar(true); abiertoAtRef.current = Date.now(); setPass(''); avisar(true, 'password')
      // t217 · tenía PIN y ha entrado con la contraseña desde el teclado: puede que no lo sepa (B-1637). Una vez por pestaña.
      if (pinUnico && pinUnico.tengo === true && verContrasena) {
        let yaDicho = false
        try { yaDicho = sessionStorage.getItem('pin_olvido_' + (user?.id || '')) === '1' } catch (_) { yaDicho = false }
        if (!yaDicho) setAvisoOlvido(true)
      }

      // ►► SI EL SERVIDOR HA DICHO QUE SÍ, SE ABRE. SIEMPRE. ◄◄
      //
      // Aquí estaba el fallo que dejó al dueño fuera de su propia aplicación tres veces. El
      // desbloqueo era: «si el aparato tiene lector y no hay huella dada de alta, enseña la tarjeta
      // de la huella» — y `bloqueado` se quedaba en true. O sea: tecleabas la contraseña BUENA, el
      // servidor respondía 200, y la pantalla seguía tapada. Como casi cualquier aparato de hoy
      // tiene lector (Windows Hello, Touch ID, huella de Android) y `huellaLista` es false también
      // cuando falta la migración de WebAuthn, la tarjeta salía CASI SIEMPRE. Y esa tarjeta era un
      // peaje sin salida: si el alta fallaba (503 por la migración), no había por dónde salir; al
      // recargar, el candado seguía puesto. La única acción que rompía el bucle era «Salir de la
      // sesión» y volver a entrar desde cero. Exactamente lo que él contaba.
      //
      // Quien decide si la contraseña basta es el SERVIDOR (/auth/verificar-password aplica también
      // la política de factor obligatorio y contesta 403 si no basta). Así que un 200 aquí solo
      // puede significar una cosa: adentro. Lo que venga después —ofrecer la huella, ofrecer el
      // PIN— se enseña ENCIMA de la aplicación ya abierta, y se puede cerrar sin dar explicaciones.
      // L21 · la marca, ANTES de destapar (como hace `abrir`): si se destapa primero, el vigilante de inactividad puede leer
      // la marca todavía puesta y volver a tapar en el acto.
      try { localStorage.removeItem(ACTIVO) } catch (_) {}
      setBloqueado(false)

      // La huella solo se ofrece si de verdad SE PUEDE dar de alta: `huellaDisponible` es false
      // cuando falta la migración, y ofrecer un botón que el servidor va a rechazar con un 503 es
      // peor que no ofrecer nada — es lo que convertía la oferta en una trampa.
      // t95 · con el PIN único, el PIN de 6 cifras lo pide su propia pantalla (abajo, «crear»): no la tarjeta de antes.
      // Y si la regla de esta persona no permite la huella, no se le ofrece darla de alta.
      const sinHuella = !!(pinUnico && (pinUnico.biometria === false || (pinUnico.regla && pinUnico.regla.biometria === false)))
      if (sinHuella) { if (!(pinUnico && pinUnico.pedir)) passRef.current = '' }
      else if (bio && !huellaLista && huellaDisponible && !navegadorPospuesto(user?.id)) setOfrecerAlta(true)
      else if (exigirFactor && !tengoPin && !pinUnico) setOfrecerPin(true)
      else if (!(pinUnico && pinUnico.pedir)) passRef.current = ''
    } catch (e) {
      // AQUI SE DECIA "Contraseña incorrecta" PARA TODO: para la contraseña mal puesta, para el
      // servidor caido, para los demasiados-intentos y — lo peor — para la SESION CADUCADA. Con el
      // token de fondo muerto, el servidor devolvia 401 SIN llegar a mirar la contraseña: la
      // persona tecleaba la BUENA, leia "incorrecta", dudaba de si misma y la tecleaba otra vez.
      // Cada caso dice ahora lo que pasa de verdad.
      const st = e?.response?.status
      if (!e?.response) {
        setError(t('components.BloqueoPantalla.no_hay_conexion_con_el', 'No hay conexión con el servidor. Espera unos segundos y vuelve a intentarlo.'))
      } else if (st === 429) {
        setError(e.response.data?.error || t('components.BloqueoPantalla.demasiados_intentos_espera_unos_minutos', 'Demasiados intentos. Espera unos minutos.'))
      } else if (st === 401) {
        // La sesion de fondo ya no vale (caducada o cortada por seguridad). El interceptor de la
        // API ya esta limpiando y llevando al login — y como las claves del bloqueo estan en la
        // lista de limpieza, alli se entra sin heredar este candado.
        setError(t('components.BloqueoPantalla.tu_sesion_ha_caducado_te', 'Tu sesión ha caducado. Te llevo a la pantalla de entrada…'))
      } else if (st === 403 && e.response.data?.motivo === 'password') {
        setError(t('components.BloqueoPantalla.contrasena_incorrecta', 'Contraseña incorrecta'))
        avisar(false, 'password')
      } else if (st === 403 && e.response.data?.motivo === 'factor_obligatorio') {
        // EL SERVIDOR MANDA. Si dice que esta cuenta ya no desbloquea con contraseña, la pantalla se
        // pone en modo estricto aunque no lo estuviera (política recién encendida, o PIN dado de
        // alta en otro aparato). Sin esto se quedaría un botón que el servidor rechaza siempre.
        setExigirFactor(true); setTengoPin(true); setPass('')
        setError(e.response.data?.error || t('components.BloqueoPantalla.tu_clinica_pide_la_huella', 'Tu clínica pide la huella o el PIN para desbloquear.'))
      } else {
        setError(e.response.data?.error || t('components.BloqueoPantalla.el_servidor_ha_tenido_un', 'El servidor ha tenido un problema. Inténtalo de nuevo.'))
      }
    } finally { setOcupado(false) }
  }

  // Al cerrar la oferta de huella (se acepte o no) todavía puede faltar el PIN. La huella sola no
  // basta cuando la clínica exige factor: es de ESTE aparato, y el de al lado puede no tener lector.
  // OJO: aquí ya NO se decide si se entra — cuando esta tarjeta se pinta, la sesión ya está
  // desbloqueada. Cerrarla solo cierra la tarjeta.
  const seguirTrasHuella = () => {
    setOfrecerAlta(false); setError('')
    if (exigirFactor && !tengoPin && passRef.current && !pinUnico) { setOfrecerPin(true); return }
    if (!(pinUnico && pinUnico.pedir)) passRef.current = ''
    tocar()
  }

  const altaHuellaAqui = async () => {
    setError(''); setOcupado(true)
    try {
      await registrarHuella(nombreDispositivo(), user?.id)
      setHuellaLista(true); posponerEnNavegador(user?.id, false); seguirTrasHuella()
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || t('components.BloqueoPantalla.no_se_ha_podido_registrar', 'No se ha podido registrar. Puedes hacerlo luego desde tu perfil.'))
    } finally { setOcupado(false) }
  }

  // Alta del PIN desde la propia pantalla de bloqueo. Aquí y no solo en un ajuste: quien está
  // delante acaba de ver que le hace falta, y es el único momento en que a alguien le apetece
  // configurarlo. Si venimos de la contraseña ya la tenemos; si no (se desbloqueó con la huella), se
  // pide — el servidor la exige para que nadie que coja un aparato desbloqueado se ponga su PIN.
  const crearPinAqui = async () => {
    // L21 · la del formulario, si el llavero la rellenó sin que React se enterase.
    const password = passRef.current || contrasenaDelFormulario(formPinRef.current, passPin)
    if (!password) return setError(t('components.BloqueoPantalla.escribe_tu_contrasena_para_guardar', 'Escribe tu contraseña para guardar el PIN'))
    if (pin.length < pinMin) return setError(t('components.BloqueoPantalla.el_pin_debe_tener_al', 'El PIN debe tener al menos {{pinMin}} cifras', { pinMin }))
    if (pin !== pin2) return setError(t('components.BloqueoPantalla.los_dos_pin_no_coinciden', 'Los dos PIN no coinciden'))
    setError(''); setOcupado(true)
    try {
      await api.put('/seguridad/desbloqueo/pin', { password, pin })
      passRef.current = ''
      setTengoPin(true); setOfrecerPin(false); setPin(''); setPin2(''); setPassPin('')
      abrir()
    } catch (e) {
      const st = e?.response?.status
      if (!e?.response) setError(t('components.BloqueoPantalla.no_hay_conexion_con_el_2', 'No hay conexión con el servidor. Inténtalo en unos segundos.'))
      else if (st === 503) {
        // Falta la migración: se dice y se deja salir, nunca se atrapa a nadie en esta pantalla.
        setError(e.response.data?.error || t('components.BloqueoPantalla.el_pin_todavia_no_esta', 'El PIN todavía no está disponible en el servidor.'))
      } else setError(e.response.data?.error || t('components.BloqueoPantalla.no_se_ha_podido_guardar', 'No se ha podido guardar el PIN'))
    } finally { setOcupado(false) }
  }

  const saltarPin = () => {
    passRef.current = ''
    setOfrecerPin(false); setPin(''); setPin2(''); setPassPin(''); setError('')
    tocar()
  }

  // OJO AL ORDEN. Antes era `if (!token || !bloqueado || esTotem) return null`, y con el arreglo de
  // arriba —la contraseña válida desbloquea SIEMPRE— eso habría hecho desaparecer también las
  // tarjetas de "date de alta la huella / el PIN", que es justo el momento en que alguien las
  // configura. Ahora la app queda ABIERTA detrás y la tarjeta se pinta encima como lo que es: una
  // oferta que se puede cerrar con "Ahora no", no un peaje para entrar.
  if (!token || esTotem) return null

  // ── t95 · LA PANTALLA DEL TECLADO ──────────────────────────────────────────────────────────
  //  Bloqueada y con PIN propio (o con «Cambiar de usuario» pedido): el teclado grande. La huella y Face ID van debajo
  //  si la regla de esta persona los permite; «Entrar con mi contraseña» y «Cerrar sesión», SIEMPRE (el dueño, 5-oct:
  //  «nadie se queda atrapado en el teclado del PIN»; el servidor tampoco veta la contraseña con el PIN único), y en
  //  «¿Quién eres?», «Entrar con otra cuenta». Otra persona que entra (con su PIN o, sin PIN, con su contraseña): su
  //  sesión NUEVA, como un inicio de sesión (login() borra lo de la anterior) y la aplicación se RECARGA entera.
  const salirDeLaSesion = () => { try { localStorage.removeItem(ACTIVO) } catch (_) {} ; avisar(true, 'salir'); logout() }
  // t169 · POR QUÉ SE HA TAPADO, en una línea, debajo del candado: con lo que dice el servidor (la regla que manda y, si es
  // la de fuera, si es por la red o por el horario). Así, si vuelve a salir en plena jornada, quien lo ve sabe qué mirar.
  const razonTexto = (() => {
    const r = razonRef.current
    const delServidor = (pinUnico && pinUnico.explicacion) || motivoServidor.motivo || ''
    if (r === 'acceso') return t('components.BloqueoPantalla.fuera_de_la_clinica_se', 'Fuera de la clínica se pide desbloquear cada vez que se vuelve a la aplicación.{{x}}', { x: (motivoServidor.fuera ? ' ' + motivoServidor.fuera : '') })
    if (r === 'inactividad') return delServidor || (t('components.BloqueoPantalla.se_ha_bloqueado_tras_min', 'Se ha bloqueado tras {{minutos}} min sin usarla.', { minutos }))
    if (r === 'otra_pestana') return t('components.BloqueoPantalla.se_ha_bloqueado_en_otra', 'Se ha bloqueado en otra pestaña de este navegador: al desbloquear aquí se abren las dos.')
    if (r === 'recarga') return t('components.BloqueoPantalla.la_aplicacion_ya_estaba_bloqueada', 'La aplicación ya estaba bloqueada antes de volver a cargar la página.')
    return ''
  })()
  const lineaRazon = razonTexto
    ? <div data-motivo-bloqueo="" style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45, marginTop: 4 }}>{razonTexto}</div>
    : null
  if (pinUnico && bloqueado && !verContrasena && (pinUnico.tengo || modoPin === 'elegir' || pinUnico.compartido === true)) {
    const biometria = !(pinUnico.regla && pinUnico.regla.biometria === false) && pinUnico.biometria !== false
    const extrasBio = biometria ? (
      <>
        {bio && huellaLista && (
          <button type="button" onClick={desbloquearHuella} disabled={ocupado}
            style={{ width: '100%', padding: '11px 14px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            {ocupado ? t('components.BloqueoPantalla.verificando', 'Verificando…') : t('components.BloqueoPantalla.desbloquear_con_huella_o_cara', 'Desbloquear con huella o cara')}
          </button>
        )}
        {nativa.lista && (
          <button type="button" data-desbloqueo="nativa" onClick={desbloquearNativa} disabled={ocupado}
            style={{ width: '100%', padding: '11px 14px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            {ocupado ? t('components.BloqueoPantalla.verificando', 'Verificando…') : t('components.BloqueoPantalla.desbloquear_con', 'Desbloquear con {{nombre}}', { nombre: nativa.nombre })}
          </button>
        )}
        {error && <div style={{ fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      </>
    ) : null
    const extras = (extrasBio || lineaRazon) ? <>{extrasBio}{lineaRazon}</> : null
    // t136 (7-oct-2026) · SIEMPRE EL TECLADO QUE RECONOCE A LA PERSONA POR SU PIN, como el quiosco (el dueño: «se pone el PIN
    // y te detecta, sin elegir antes quién eres»): también al abrir «Cambiar de usuario» y en un puesto compartido, donde
    // solo cambian los rótulos (`cambio`). Quien aún no tiene PIN tiene debajo su lista y su contraseña.
    return (
      <PantallaDelPin api={api} usuario={user} modo="desbloquear" cambio={modoPin === 'elegir' || pinUnico.compartido === true}
        puedeCambiar={pinUnico.cambio_usuario === true} avisoCambio={pinUnico.cambio_usuario_motivo || null}
        yoTengoPin={pinUnico.tengo === true} extras={extras}
        onAbierto={(d) => { abrir(); setPass(''); setError(''); avisar(true, 'pin'); avisarUnico(d) }}
        onSesionNueva={(d) => {
          try { localStorage.removeItem(ACTIVO) } catch (_) {}
          if (typeof login === 'function') login(d.agente, d.token)
          window.location.href = d.agente && d.agente.rol === 'control_horario' ? '/fichar' : '/'
        }}
        onContrasena={() => { setError(''); setVerContrasena(true) }}
        onSalir={salirDeLaSesion} onOtraCuenta={salirDeLaSesion} />
    )
  }
  //  Sin PIN propio todavía y en un PUESTO COMPARTIDO (aquí se entra con el PIN): se le ofrece ponerlo YA. Encima de la
  //  aplicación abierta, nunca antes de abrirla: si la sesión está bloqueada, primero se desbloquea con la contraseña (la
  //  tarjeta de siempre) y luego sale esto, ya con la contraseña recién escrita (no se vuelve a pedir). «Salir de la sesión»
  //  siempre. t217 (13-oct-2026, B-1637) · Y «AHORA NO» TAMBIÉN AQUÍ: obligar a crearlo en ese momento, sin otra salida que
  //  cerrar la sesión, es lo que acababa con alguien tecleando seis cifras cualquiera para poder seguir, y con un PIN que no
  //  sabe. Con «Ahora no» no se vuelve a ofrecer en esta pestaña (al volver a entrar, otra vez); en Mi perfil y en la página
  //  del enlace del correo, que ya son para crearlo, no sale.
  const fueraOferta = () => {
    setOfertaFuera(true); setRecordatorioFuera(true)
    try { sessionStorage.setItem('pin_ofrecido_' + (user?.id || ''), '1'); sessionStorage.setItem('pin_recordado_' + (user?.id || ''), '1') } catch (_) { /* sin almacén */ }
  }
  if (pinUnico && pinUnico.pedir && !bloqueado && !ofrecerAlta && !ofertaFuera
      && !/^\/(perfil|crear-pin\/)/.test(String((window.location && window.location.pathname) || ''))) {
    const yaConContrasena = !!passRef.current
    return (
      <PantallaDelPin api={api} usuario={user} modo="crear"
        pedirContrasena={pinUnico.pedir_contrasena === true && !yaConContrasena}
        contrasenaInicial={passRef.current || ''} campoUsuario={usuarioDeLaSesion(user?.email)}
        onCreado={() => { passRef.current = ''; fueraOferta(); setPinUnico((p) => (p ? { ...p, tengo: true, pedir: false, provisional: false } : p)); tocar() }}
        onAhoraNo={() => { passRef.current = ''; fueraOferta() }} onSalir={salirDeLaSesion} />
    )
  }

  //  t95c → t217 · RECIÉN ENTRADO CON LA CONTRASEÑA y sin PIN: ya NO sale la pantalla entera de ponerlo (se parecía a la de
  //  desbloquear y guardaba sin pedir la contraseña: B-1637). Se le ofrece con el recordatorio de abajo, «Crear mi PIN», que
  //  lleva a Mi perfil, Tu PIN.

  //  t302 · LOS TRES AVISOS DE ABAJO («¿No recuerdas tu PIN?», «Ahora tienes un solo PIN para todo» y el recordatorio de
  //  crearlo) no tapan nada: van en la capa de los avisos que flotan (index.css, «LAS CAPAS DE LA APLICACIÓN»). Con 9000
  //  quedaban encima de las ventanas y de un desplegable de la barra recién abierto.
  //  t217 · «¿NO RECUERDAS TU PIN?». Quien tiene PIN y acaba de entrar con su contraseña desde el teclado («Entrar con mi
  //  contraseña»): si es porque no lo sabe (lo puso sin darse cuenta, B-1637), se le dice dónde poner otro con la contraseña.
  if (avisoOlvido && !bloqueado && !ofrecerAlta && !ofrecerPin && !/^\/(perfil|crear-pin\/)/.test(String((window.location && window.location.pathname) || ''))) {
    const cerrarOlvido = () => {
      setAvisoOlvido(false)
      try { sessionStorage.setItem('pin_olvido_' + (user?.id || ''), '1') } catch (_) { /* sin almacén */ }
    }
    return (
      <div data-aviso-pin-olvidado="" role="status"
        style={{ position: 'fixed', left: 16, right: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 'var(--capa-aviso-flotante)', maxWidth: 380, background: 'var(--surface, #fff)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 10px 30px rgba(16,24,40,.16)', padding: '14px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>{t('components.BloqueoPantalla.no_recuerdas_tu_pin', '¿No recuerdas tu PIN?')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
          {t('components.BloqueoPantalla.has_entrado_con_tu_contrasena', 'Has entrado con tu contraseña. Si no sabes tu PIN, pon uno nuevo en Mi perfil, Tu PIN, con tu contraseña: el de antes deja de valer.')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/perfil?tab=seguridad&pin=1" onClick={cerrarOlvido} style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>{t('components.BloqueoPantalla.poner_otro_pin', 'Poner otro PIN')}</a>
          <button type="button" data-aviso-pin-olvidado-entendido="" onClick={cerrarOlvido}
            style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.entendido', 'Entendido')}
          </button>
        </div>
      </div>
    )
  }

  //  t95 · «AHORA TIENES UN SOLO PIN PARA TODO»: una sola vez, al pasar su PIN de desbloqueo de antes a PIN para todo.
  if (avisoUnico && !bloqueado) {
    return (
      <div data-aviso-pin-unico={avisoUnico} role="status"
        style={{ position: 'fixed', left: 16, right: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 'var(--capa-aviso-flotante)', maxWidth: 380, background: 'var(--surface, #fff)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 10px 30px rgba(16,24,40,.16)', padding: '14px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>{avisoUnico === 'unificado' ? t('components.BloqueoPantalla.ahora_tienes_un_solo_pin', 'Ahora tienes un solo PIN para todo') : t('components.BloqueoPantalla.elige_tu_pin_de_6', 'Elige tu PIN de 6 cifras')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
          {avisoUnico === 'unificado'
            ? t('components.BloqueoPantalla.el_pin_con_el_que', 'El PIN con el que acabas de desbloquear vale también para fichar en el quiosco y para «Cambiar de usuario». Tu PIN de fichar de antes sigue fichando hasta que cambies el tuyo en Mi perfil.')
            : t('components.BloqueoPantalla.ahora_hay_un_solo_pin', 'Ahora hay un solo PIN para todo, de 6 cifras. El que acabas de usar no puede serlo (tiene otro largo, es demasiado fácil o ya lo usa otra persona): elige el tuyo en Mi perfil.')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {avisoUnico !== 'unificado' && <a href="/perfil?tab=seguridad&pin=1" style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>{t('components.BloqueoPantalla.elegir_mi_pin', 'Elegir mi PIN')}</a>}
          <button type="button" data-aviso-pin-unico-entendido="" onClick={() => setAvisoUnico(null)}
            style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.entendido', 'Entendido')}
          </button>
        </div>
      </div>
    )
  }

  //  Sin PIN propio y sin que aquí haga falta: un RECORDATORIO que no bloquea nada (el dueño: «a quien no tenga PIN se le
  //  recuerda hasta que lo cree»). «Ahora no» lo esconde hasta la próxima vez que se abra la aplicación; en Mi perfil no
  //  sale (allí está el apartado para ponerlo).
  if (pinUnico && pinUnico.recordar && !bloqueado && !ofrecerAlta && !ofrecerPin && !recordatorioFuera
      && !/^\/(perfil|crear-pin\/)/.test(String((window.location && window.location.pathname) || ''))) {
    return (
      <div data-recordatorio-pin="" role="status"
        style={{ position: 'fixed', left: 16, right: 16, bottom: 'calc(16px + var(--sa-abajo))', zIndex: 'var(--capa-aviso-flotante)', maxWidth: 360, background: 'var(--surface, #fff)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 10px 30px rgba(16,24,40,.16)', padding: '14px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>{pinUnico.desbloqueo_viejo || pinUnico.fichaje_viejo ? t('components.BloqueoPantalla.ahora_tienes_un_solo_pin', 'Ahora tienes un solo PIN para todo') : t('components.BloqueoPantalla.crea_tu_pin_de_6', 'Crea tu PIN de 6 cifras')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
          {pinUnico.desbloqueo_viejo
            ? t('components.BloqueoPantalla.la_proxima_vez_que_desbloquees', 'La próxima vez que desbloquees con tu PIN de siempre, pasará a valer también para fichar y para «Cambiar de usuario». O crea uno nuevo en Mi perfil.')
            : pinUnico.fichaje_viejo
              ? t('components.BloqueoPantalla.tu_pin_de_fichar_sigue', 'Tu PIN de fichar sigue valiendo para fichar. Crea el tuyo en Mi perfil: con él fichas, desbloqueas la aplicación y entras en cualquier ordenador de la clínica con «Cambiar de usuario».')
              : t('components.BloqueoPantalla.con_el_fichas_desbloqueas_la', 'Con él fichas, desbloqueas la aplicación y entras en cualquier ordenador de la clínica con «Cambiar de usuario». Se pone en Mi perfil.')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/perfil?tab=seguridad&pin=1" style={{ padding: '8px 12px', borderRadius: 9, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>{t('components.BloqueoPantalla.crear_mi_pin', 'Crear mi PIN')}</a>
          <button type="button" onClick={() => { setRecordatorioFuera(true); try { sessionStorage.setItem('pin_recordado_' + (user?.id || ''), '1') } catch (_) {} }}
            style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.ahora_no', 'Ahora no')}
          </button>
        </div>
      </div>
    )
  }

  if (!bloqueado && !ofrecerAlta && !ofrecerPin) return null

  // ¿Se le puede EXIGIR el factor a quien está delante? Solo si tiene con qué desbloquear aquí:
  // PIN (vale en cualquier aparato) o huella registrada Y lector en este. Exigirlo sin eso sería
  // dejar a alguien fuera de la app con un paciente en el sillón, y eso no se hace nunca: si no hay
  // factor posible, la contraseña vuelve a salir aunque la clínica tenga el modo encendido.
  // L21 · y la cara o el dedo de la app del móvil, que también es «algo que tienes».
  const puedeFactor = tengoPin || (bio && huellaLista) || nativa.lista
  // t95 · con el PIN único la contraseña desbloquea SIEMPRE (el dueño, 5-oct): el campo no se esconde nunca.
  const estricto = exigirFactor && puedeFactor && !pinUnico

  // ACABA DE ESCRIBIR LA CONTRASEÑA en un móvil, con prisa. Es el único momento en que a alguien le
  // apetece configurar la huella: en un ajuste perdido no lo activa nadie, y entonces el bloqueo
  // cada pocos minutos se vuelve insufrible y se acaba desactivando el bloqueo entero.
  // "Ahora no" cierra sin insistir y no se vuelve a ofrecer en este navegador en 30 días (utils/huellaPospuesta, el dueño:
  // «una vez, sin agobiar»); siempre está en Mi perfil.
  if (ofrecerAlta) {
    return (
      <div className="modal-safe" style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--surface-2, #f4f6f8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: 'var(--surface, #fff)', borderRadius: 18, boxShadow: '0 18px 50px rgba(0,0,0,.18)', padding: 26, width: '100%', maxWidth: 380, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><path d="M12 11v3a6 6 0 0 1-1 3.3" /><path d="M8.5 9.5a4 4 0 0 1 7 2.5v2" /><path d="M5.5 8a8 8 0 0 1 13 3.5" /><path d="M15 20a12 12 0 0 0 1.5-6" /><path d="M8 21a10 10 0 0 0 1.5-4" /></svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{t('components.BloqueoPantalla.desbloquear_con_la_huella', '¿Desbloquear con la huella?')}</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '8px 0 18px', lineHeight: 1.5 }}>
            {tx('components.BloqueoPantalla.la_proxima_vez_entras_con', 'La próxima vez entras con el dedo (o la cara) en vez de escribir la contraseña. Tu huella no sale de este {{toLowerCase}}: la clínica no la ve ni la guarda.', { toLowerCase: nombreDispositivo().toLowerCase() })}
          </p>
          <button onClick={altaHuellaAqui} disabled={ocupado}
            style={{ width: '100%', padding: '12px 16px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' }}>
            {ocupado ? t('components.BloqueoPantalla.registrando', 'Registrando…') : t('components.BloqueoPantalla.activar_la_huella_aqui', 'Activar la huella aquí')}
          </button>
          <button onClick={() => { posponerEnNavegador(user?.id, true); seguirTrasHuella() }} disabled={ocupado}
            style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.ahora_no', 'Ahora no')}
          </button>
          {error && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
        </div>
      </div>
    )
  }

  // ── DAR DE ALTA EL PIN ──────────────────────────────────────────────────────────────────────
  // Sale cuando la clínica exige huella o PIN y esta persona todavía no tiene PIN. Es el respaldo
  // que funciona en TODOS los aparatos: sin él, el primer ordenador sin lector la deja con "Salir de
  // la sesión" como única salida, y eso, repetido, acaba con alguien apagando el bloqueo entero.
  if (ofrecerPin) {
    const inpPin = { width: '100%', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 17, letterSpacing: 6, textAlign: 'center', fontWeight: 800, background: 'var(--surface)', color: 'var(--text-primary)', boxSizing: 'border-box' }
    return (
      <div className="modal-safe" style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--surface-2, #f4f6f8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: 'var(--surface, #fff)', borderRadius: 18, boxShadow: '0 18px 50px rgba(0,0,0,.18)', padding: 26, width: '100%', maxWidth: 380, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <Ico n="pin" s={26} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{t('components.BloqueoPantalla.crea_tu_pin_de_desbloqueo', 'Crea tu PIN de desbloqueo')}</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '8px 0 16px', lineHeight: 1.5 }}>
            {t('components.BloqueoPantalla.tu_clinica_pide_huella_o', 'Tu clínica pide huella o PIN para desbloquear la app. El PIN es tu respaldo: sirve en cualquier ordenador o tablet, aunque no tenga lector de huella.')}
            <b>{t('components.BloqueoPantalla.no_es_el_pin_de', ' No es el PIN de fichar')}</b>{t('components.BloqueoPantalla.ese_lo_tecleas_en_la', ': ese lo tecleas en la tablet delante de todos.')}
          </p>
          {/* Con InputPassword, no con un input pelado: teclear a ciegas un PIN en un movil, con
              prisa y a veces con guantes, es donde mas se falla — y el ojo es justo lo que evita el
              bucle de "PIN incorrecto" sin saber que se ha escrito mal. */}
          {/* L21 · el PIN no es una contraseña: «off», no «new-password». Con «new-password» el iPhone propone guardar
              el PIN como contraseña nueva de la cuenta (y sugiere una contraseña fuerte en vez de un PIN). */}
          <InputPassword value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
            inputMode="numeric" autoComplete="off" name="pin-desbloqueo" autoFocus
            placeholder={'•'.repeat(pinMin)} style={inpPin} />
          <InputPassword value={pin2} onChange={e => setPin2(e.target.value.replace(/\D/g, '').slice(0, 10))}
            inputMode="numeric" autoComplete="off" name="pin-desbloqueo-repetido"
            onKeyDown={e => e.key === 'Enter' && crearPinAqui()}
            placeholder={t('components.BloqueoPantalla.repitelo', 'Repítelo')} style={{ ...inpPin, marginTop: 8 }} />
          {/* Si se desbloqueó con la huella no tenemos su contraseña, y el servidor la exige: sin
              ella, quien cogiera un aparato desbloqueado se pondría su propio PIN.
              L21 · en su propio formulario, con el usuario de esta sesión: así el llavero del iPhone la ofrece. */}
          {!passRef.current && (
            <form ref={formPinRef} data-desbloqueo="contrasena-del-pin" onSubmit={e => { e.preventDefault(); crearPinAqui() }}
              style={{ marginTop: 8, textAlign: 'left' }}>
              <input {...usuarioDeLaSesion(user?.email)} />
              <InputPassword value={passPin} onChange={e => setPassPin(e.target.value)} placeholder={t('components.BloqueoPantalla.tu_contrasena', 'Tu contraseña')}
                name="password" autoComplete="current-password"
                style={{ padding: '11px 13px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, background: 'var(--surface)', color: 'var(--text-primary)' }} />
            </form>
          )}
          <button onClick={crearPinAqui} disabled={ocupado}
            style={{ width: '100%', marginTop: 12, padding: '12px 16px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' }}>
            {ocupado ? t('components.BloqueoPantalla.guardando', 'Guardando…') : t('components.BloqueoPantalla.guardar_mi_pin', 'Guardar mi PIN')}
          </button>
          {/* "Ahora no" SIEMPRE. Obligar a crear el PIN en esta pantalla sería encerrar aquí a quien
              no se acuerde de su contraseña, con la app tapada. Se vuelve a ofrecer la próxima vez. */}
          <button onClick={saltarPin} disabled={ocupado}
            style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.ahora_no', 'Ahora no')}
          </button>
          {error && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="modal-safe" style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--surface-2, #f4f6f8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 18, boxShadow: '0 18px 50px rgba(0,0,0,.18)', padding: 26, width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{t('components.BloqueoPantalla.sesion_bloqueada', 'Sesión bloqueada')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 16 }}>
          {tx('components.BloqueoPantalla.desbloquea_para_continuar', '{{x}} · desbloquea para continuar{{lineaRazon}}', { x: [user?.nombre, user?.apellidos].filter(Boolean).join(' ') || t('components.BloqueoPantalla.tu_sesion', 'Tu sesión'), lineaRazon })}
        </div>

        {/* SOLO SI HAY UNA HUELLA REGISTRADA. Antes salía con que el aparato tuviera lector, y como
            nunca se registraba ninguna, el botón fallaba siempre: se pulsaba, salía "no se ha podido
            verificar" y se acababa escribiendo la contraseña igualmente. Un botón que nunca funciona
            es peor que no tenerlo, porque además hace desconfiar del que sí funciona. */}
        {bio && huellaLista && (
          <button onClick={desbloquearHuella} disabled={ocupado}
            style={{ width: '100%', padding: '12px 16px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14.5, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
            {ocupado ? t('components.BloqueoPantalla.verificando', 'Verificando…') : t('components.BloqueoPantalla.desbloquear_con_huella_o_cara', 'Desbloquear con huella o cara')}
          </button>
        )}

        {/* L21 · EN LA APP DEL MÓVIL: la cara o el dedo, con la llave de «Entrar con Face ID o huella». Desbloquea ESTA
            sesión (lo comprueba el servidor); si se cancela, la contraseña sigue debajo. */}
        {nativa.lista && (
          <button type="button" data-desbloqueo="nativa" onClick={desbloquearNativa} disabled={ocupado}
            style={{ width: '100%', padding: '12px 16px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14.5, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
            {ocupado ? t('components.BloqueoPantalla.verificando', 'Verificando…') : t('components.BloqueoPantalla.desbloquear_con', 'Desbloquear con {{nombre}}', { nombre: nativa.nombre })}
          </button>
        )}

        {/* SE INTENTÓ SOLA Y EL NAVEGADOR NO DEJÓ. No es un error de nadie y no va en rojo: es
            Safari pidiendo que la huella salga de un gesto, o Chrome con la pestaña sin el foco.
            Sin esta línea, quien ya se acostumbró a que salga sola creería que se ha roto. */}
        {bio && huellaLista && autoImposible && (
          <div style={{ marginTop: -2, marginBottom: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {t('components.BloqueoPantalla.tu_navegador_no_deja_pedirte', 'Tu navegador no deja pedirte la huella sola: pulsa el botón y sale igual.')}
          </div>
        )}

        {/* EL PIN, cuando la clínica exige factor. Va oculto (type password) porque en recepción
            siempre hay alguien detrás: un PIN a la vista no es un segundo factor. */}
        {(estricto || !!(pinUnico && pinUnico.desbloqueo_viejo)) && tengoPin && (
          <>
            <InputPassword value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputMode="numeric" autoComplete="off" placeholder={t('components.BloqueoPantalla.tu_pin', 'Tu PIN')}
              onKeyDown={e => e.key === 'Enter' && desbloquearPin()}
              style={{ width: '100%', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 17, letterSpacing: 6, textAlign: 'center', fontWeight: 800, background: 'var(--surface)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
            <button onClick={desbloquearPin} disabled={ocupado}
              style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {t('components.BloqueoPantalla.desbloquear_con_el_pin', 'Desbloquear con el PIN')}
            </button>
          </>
        )}

        {/* LA CONTRASEÑA SOLO CUANDO NO HAY OTRA COSA. Este era el agujero: el atajo estaba siempre,
            y es la misma contraseña que el navegador rellena solo. Con el modo estricto encendido y
            un factor disponible aquí, deja de ofrecerse — y el servidor la rechaza a quien tenga PIN,
            para que esto no sea solo pintura de pantalla.
            Con el móvil en la mano y prisa, teclear a ciegas es donde más se falla: por eso el ojo. */}
        {!estricto && (
          <form data-desbloqueo="contrasena" onSubmit={e => { e.preventDefault(); desbloquearPass(contrasenaDelFormulario(e.currentTarget, pass)) }} style={{ margin: 0 }}>
            <input {...usuarioDeLaSesion(user?.email)} />
            <InputPassword value={pass} onChange={e => setPass(e.target.value)} placeholder={t('components.BloqueoPantalla.tu_contrasena', 'Tu contraseña')}
              name="password" autoComplete="current-password"
              style={{ padding: '11px 13px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, background: 'var(--surface)', color: 'var(--text-primary)' }} />
            {/* L21 · UN FORMULARIO DE VERDAD, con el usuario de esta sesión oculto (utils/reautenticar): es lo que hace
                que el llavero del iPhone ofrezca ESTA contraseña. Y al enviarlo se lee la casilla, no solo el estado. */}
            <button type="submit" disabled={ocupado}
              style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {t('components.BloqueoPantalla.desbloquear_con_contrasena', 'Desbloquear con contraseña')}
            </button>
          </form>
        )}

        {/* Por qué no sale el campo de siempre. Sin esta línea, quien lleve un año escribiendo la
            contraseña aquí cree que la app se ha roto. */}
        {estricto && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {tx('components.BloqueoPantalla.tu_clinica_pide_para_desbloquear', 'Tu clínica pide {{x}} para desbloquear: la contraseña que guarda el navegador ya no abre la sesión.{{x2}}', { x: tengoPin && bio && huellaLista ? t('components.BloqueoPantalla.la_huella_o_el_pin', 'la huella o el PIN') : tengoPin ? t('components.BloqueoPantalla.el_pin', 'el PIN') : t('components.BloqueoPantalla.la_huella', 'la huella'), x2: !tengoPin && t('components.BloqueoPantalla.si_aqui_no_puedes_usar', ' Si aquí no puedes usar la huella, sal de la sesión y entra de nuevo.') })}
          </div>
        )}

        {/* La clínica lo exige pero esta persona no tiene con qué: sigue entrando con la contraseña
            (nadie se queda fuera) y se le dice que al desbloquear se le ofrece configurarlo. */}
        {exigirFactor && !puedeFactor && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {t('components.BloqueoPantalla.tu_clinica_pide_huella_o_2', 'Tu clínica pide huella o PIN para desbloquear y todavía no tienes ninguno. Al entrar te ofrezco configurarlo: son diez segundos y deja de valer la contraseña guardada.')}
          </div>
        )}

        {error && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}

        {/* t95 · «Cambiar de usuario» también desde aquí (quien aún no tiene su PIN desbloquea con la contraseña), y la
            vuelta al teclado para quien ha pulsado «Usar mi contraseña». */}
        {pinUnico && pinUnico.cambio_usuario === true && (
          <button type="button" data-cambiar-usuario-tarjeta="" onClick={() => { setError(''); setVerContrasena(false); setModoPin('elegir') }}
            style={{ width: '100%', marginTop: 10, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            {t('components.BloqueoPantalla.cambiar_de_usuario', 'Cambiar de usuario')}
          </button>
        )}
        {pinUnico && pinUnico.tengo && verContrasena && (
          <button type="button" onClick={() => { setError(''); setVerContrasena(false) }}
            style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>
            {t('components.BloqueoPantalla.volver_al_teclado_del_pin', 'Volver al teclado del PIN')}
          </button>
        )}

        {/* Al salir se borra la marca de bloqueado: quien entre despues empieza limpio y no hereda
            una pantalla bloqueada que no es suya. La SALIDA siempre tiene que funcionar — es lo que
            impide que un fallo aqui deje al equipo fuera de la app en mitad de la consulta. */}
        <button onClick={() => { try { localStorage.removeItem(ACTIVO) } catch (_) {} ; avisar(true, 'salir'); logout() }}
          style={{ marginTop: 14, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>
          {t('components.BloqueoPantalla.salir_de_la_sesion', 'Salir de la sesión')}
        </button>
      </div>
    </div>
  )
}
